/**
 * Per-user state — recents, pinned entities, density, mode, graphContrast
 * (doc 147 §9 Phase 1; §8.9.6 revised for theming).
 *
 * Persisted to Firestore `/users/{uid}/...` so it survives device switches.
 * Pure list helpers are unit-tested; the Firestore read/write wrapper is
 * thin and degrades gracefully (an unreachable Firestore must never break
 * navigation — doc memory: every dependency needs a graceful fallback).
 */
import type { DensityMode } from '../theme/cssVariables.js';
import type { GraphContrast } from '../theme/tokens.js';
import type { ModeSetting } from './appearance.js';
import type { ViewMode } from './store.js';

export const MAX_RECENTS = 12;

/** Add an entity to the front of the recents list, de-duped and capped. */
export function addRecent(recents: string[], entityId: string, max = MAX_RECENTS): string[] {
  const next = [entityId, ...recents.filter((id) => id !== entityId)];
  return next.slice(0, max);
}

/** Toggle an entity's pinned state. */
export function togglePin(pinned: string[], entityId: string): string[] {
  return pinned.includes(entityId)
    ? pinned.filter((id) => id !== entityId)
    : [...pinned, entityId];
}

export interface UserStateDoc {
  recents: string[];
  pinned: string[];
  density: DensityMode;
  mode: ModeSetting;
  graphContrast: GraphContrast;
  view: ViewMode;
}

export const defaultUserState: UserStateDoc = {
  recents: [],
  pinned: [],
  density: 'compact',
  mode: 'auto',
  graphContrast: 'standard',
  view: '2d',
};

const MODES: readonly ModeSetting[] = ['auto', 'dark', 'light'];
const CONTRASTS: readonly GraphContrast[] = ['standard', 'high'];

/** Coerce an untrusted Firestore document into a valid {@link UserStateDoc}. */
export function normalizeUserState(raw: unknown): UserStateDoc {
  if (typeof raw !== 'object' || raw === null) return { ...defaultUserState };
  const r = raw as Record<string, unknown>;
  const strList = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  const density: DensityMode =
    r.density === 'regular' || r.density === 'expanded' || r.density === 'compact'
      ? r.density
      : 'compact';
  const mode: ModeSetting = (MODES as readonly string[]).includes(r.mode as string)
    ? (r.mode as ModeSetting)
    : 'auto';
  const graphContrast: GraphContrast = (CONTRASTS as readonly string[]).includes(
    r.graphContrast as string,
  )
    ? (r.graphContrast as GraphContrast)
    : 'standard';
  const view: ViewMode = r.view === '3d' ? '3d' : '2d';
  return {
    recents: strList(r.recents).slice(0, MAX_RECENTS),
    pinned: strList(r.pinned),
    density,
    mode,
    graphContrast,
    view,
  };
}
